import { useMemo, useState } from "react";
import {
  BroadcastManyQuery,
  EnumOwnerType,
  useBroadcastManyLazyQuery,
  usePersonDetailsQuery,
} from "gql/generated";
import { useLoggedInAs } from "shared/hooks";
import { useTranslation } from "react-i18next";
import { useStorefrontView } from "apps/storefront/StorefrontViewContext";

export enum ViewKey {
  INFO = "INFO",
  TIMELINE = "TIMELINE",
}

type UsePersonDetailsModalProps = {
  personId: string | null;
};

export const usePersonDetailsModal = ({
  personId,
}: UsePersonDetailsModalProps) => {
  const { t } = useTranslation("people", {
    keyPrefix: "PERSON_DETAILS_MODAL.VIEWS",
  });
  const loggedInAs = useLoggedInAs();

  const {
    data,
    loading,
    error,
    refetch: refetchPerson,
  } = usePersonDetailsQuery({
    variables: {
      personId: personId ?? "",
      ...(loggedInAs && loggedInAs.entityId
        ? {
            entityId: loggedInAs.entityId,
            type: loggedInAs.type,
          }
        : undefined),
    },
    skip: !personId,
  });

  const [isManageTagsModalOpen, setIsManageTagsModalOpen] = useState(false);
  const [isManualOrderModalOpen, setIsManualOrderModalOpen] = useState(false);
  const [isContactInfoModalOpen, setIsContactInfoModalOpen] = useState(false);
  const [selectedLeadId, setSelectedLeadId] = useState<string | null>(null);
  const [isEventCreateEditModalOpen, setIsEventCreateEditModalOpen] =
    useState(false);
  const [selectedEventId, setSelectedEventId] = useState<string>("");
  const [isBroadcastModalOpen, setIsBroadcastModalOpen] = useState(false);
  const [selectedBroadcast, setSelectedBroadcast] = useState<
    BroadcastManyQuery["broadcastMany"][0] | null
  >(null);
  const [initialResourceIdForBroadcast, setInitialResourceIdForBroadcast] =
    useState<string | null>(null);
  const [selectedSurveyResponseId, setSelectedSurveyResponseId] = useState<
    string | null
  >(null);
  const [loadingTimelineItemId, setLoadingTimelineItemId] =
    useState<string>("");
  const { storefrontId: contextStorefrontId } = useStorefrontView();
  const isStorefront =
    loggedInAs?.type === EnumOwnerType.Storefront || !!contextStorefrontId;
  const storefrontId = isStorefront
    ? loggedInAs?.type === EnumOwnerType.Storefront
      ? loggedInAs?.entityId
      : contextStorefrontId
    : null;

  const [selectedView, setSelectedView] = useState<ViewKey>(ViewKey.INFO);
  const views = useMemo(
    () =>
      Object.values(ViewKey).map(value => ({
        label: t(value),
        value,
      })),
    [t],
  );

  const handleOpenEventModal = (eventId: string) => {
    setIsEventCreateEditModalOpen(true);
    setSelectedEventId(eventId);
  };

  const handleCloseEventModal = () => {
    setIsEventCreateEditModalOpen(false);
    setSelectedEventId("");
  };

  const [getBroadcastData] = useBroadcastManyLazyQuery();

  const handleOpenBroadcastModal = async (
    broadcastId: string,
    timelineItemId: string,
  ) => {
    setLoadingTimelineItemId(timelineItemId);
    if (broadcastId.length > 0) {
      const { data: broadcastData } = await getBroadcastData({
        variables: {
          filter: {
            _id: broadcastId,
          },
          limit: 1,
        },
      });

      if (
        broadcastData?.broadcastMany &&
        broadcastData.broadcastMany.length > 0
      ) {
        setSelectedBroadcast(broadcastData.broadcastMany[0]);
        setIsBroadcastModalOpen(true);
      }
    }
    setLoadingTimelineItemId("");
  };

  const handleCloseBroadcastModal = () => {
    setIsBroadcastModalOpen(false);
    setSelectedBroadcast(null);
    setInitialResourceIdForBroadcast(null);
  };

  const handleOpenBroadcastFromLead = async (
    broadcastId: string,
    resourceId: string,
  ) => {
    if (!broadcastId) return;
    const { data: broadcastData } = await getBroadcastData({
      variables: {
        filter: { _id: broadcastId },
        limit: 1,
      },
    });
    if (
      broadcastData?.broadcastMany &&
      broadcastData.broadcastMany.length > 0
    ) {
      setSelectedBroadcast(broadcastData.broadcastMany[0]);
      setInitialResourceIdForBroadcast(resourceId || null);
      setIsBroadcastModalOpen(true);
    }
  };

  const handleOpenFormResponseModal = (surveyResponseId: string) => {
    setSelectedLeadId(null);
    setSelectedSurveyResponseId(surveyResponseId);
  };

  return {
    person: personId
      ? data?.personDetails
      : // Avoid breaking the UI
        {
          _id: "",
          personInfo: {
            email: "",
            firstName: "",
            lastName: "",
            phone: "",
            systemRoles: [],
            tags: [],
          },
          operators: [],
          phones: [],
          emails: [],
        },
    loading,
    error,
    storefrontId,
    isManageTagsModalOpen,
    setIsManageTagsModalOpen,
    isManualOrderModalOpen,
    setIsManualOrderModalOpen,
    isContactInfoModalOpen,
    setIsContactInfoModalOpen,
    selectedLeadId,
    setSelectedLeadId,
    refetchPerson,
    views,
    selectedView,
    setSelectedView,
    isEventCreateEditModalOpen,
    selectedEventId,
    onOpenEventModal: handleOpenEventModal,
    onCloseEventModal: handleCloseEventModal,
    isBroadcastModalOpen,
    selectedBroadcast,
    loadingTimelineItemId,
    onOpenBroadcastModal: handleOpenBroadcastModal,
    onCloseBroadcastModal: handleCloseBroadcastModal,
    initialResourceIdForBroadcast,
    onOpenBroadcastFromLead: handleOpenBroadcastFromLead,
    selectedSurveyResponseId,
    setSelectedSurveyResponseId,
    onOpenFormResponseModal: handleOpenFormResponseModal,
  };
};
